import { useEffect, useState } from "react";

/**
 * Hands-free Commute Mode detector.
 *
 * Auto-activates when ANY of:
 *   - Device is offline (navigator.onLine === false)
 *   - Device is in landscape orientation AND viewport is mobile-sized
 *     (proxy for "phone in pocket / on stand with headphones plugged in" —
 *     browsers don't expose true headphone state)
 *
 * Plus an explicit user override: { off, on, auto } — defaults to `auto`.
 *
 * Returns:
 *   {
 *     active: boolean,                       // final effective state
 *     auto: boolean,                         // true if currently triggered by detection
 *     reasons: string[],                     // e.g. ["offline", "landscape"]
 *     mode: "auto" | "on" | "off",           // current user override
 *     setMode: (m) => void,                  // change override
 *     isOnline: boolean,
 *     isLandscapeMobile: boolean,
 *   }
 */
export function useCommuteMode() {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("pm_commute_mode") || "auto"; } catch { return "auto"; }
  });
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("pm_commute_mode", mode); } catch { /* noop */ }
  }, [mode]);

  // Online / offline
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Landscape on a mobile-sized viewport — single combined media query so we
  // re-evaluate on rotate AND on resize.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(orientation: landscape) and (max-height: 500px)");
    const apply = () => setIsLandscapeMobile(mq.matches);
    apply();
    // Modern + legacy listener APIs
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  const reasons = [];
  if (!isOnline) reasons.push("offline");
  if (isLandscapeMobile) reasons.push("landscape");
  const autoTriggered = reasons.length > 0;

  let active = false;
  if (mode === "on") active = true;
  else if (mode === "off") active = false;
  else active = autoTriggered;

  return {
    active,
    auto: mode === "auto" && autoTriggered,
    reasons,
    mode,
    setMode,
    isOnline,
    isLandscapeMobile,
  };
}

/** Commute Mode overrides — applied on top of saved audio settings. */
export const COMMUTE_OVERRIDES = {
  read_aloud_enabled: true,
  reading_flow: "continuous",
  reading_speed: "slow",
  delay_before_answer_seconds: 3,
  delay_before_next_question_seconds: 3,
};

export function applyCommuteOverrides(settings) {
  return { ...settings, ...COMMUTE_OVERRIDES };
}
